import { useEffect, useState, type RefObject } from 'react';

/**
 * Put the cursor in an input that may not exist yet.
 *
 * "Add copy" adds a row and wants the cursor in it, but the row is only in the
 * DOM after the render the click triggers — focusing straight away finds
 * nothing and leaves focus on the button. So the request is remembered, and
 * acted on after the render that sets it, which is also the render that draws
 * the row: callers ask in the same update that adds it. The target is the last
 * input under *container* matching *selector*, scrolled into view and focused.
 *
 * Shared by every "take me to the copy" in the detail overlay: a carousel
 * frame's row and a master image's metadata form.
 */
export function useFocusWhenRendered(
  container: RefObject<HTMLElement | null>,
  selector = 'input[type="text"]',
): () => void {
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!pending) return;
    const inputs = container.current?.querySelectorAll<HTMLInputElement>(selector);
    const target = inputs?.[inputs.length - 1];
    if (!target) return;
    // Optional call: jsdom has none, and the cursor must land regardless.
    target.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    target.focus({ preventScroll: true });
    setPending(false);
  }, [pending, container, selector]);

  return () => setPending(true);
}
